import { useState, FormEvent } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function ContactPage() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    try {
      const response = await fetch('https://readdy.ai/api/form/d69punlbd17lru29ldt0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormSubmitted(true);
        form.reset();
        setCharCount(0);
      }
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactDetails = [
    {
      icon: 'ri-map-pin-2-line',
      label: 'Office Address',
      lines: ['969 Third Ave., Suite #4F', 'New York, NY 10022'],
      href: null,
    },
    {
      icon: 'ri-phone-line',
      label: 'Phone',
      lines: ['917-432-0310'],
      href: 'tel:9174320310',
    },
    {
      icon: 'ri-mail-line',
      label: 'Email',
      lines: ['office@reikencm.com'],
      href: 'mailto:office@reikencm.com',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative h-[55vh] w-full overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/7e244a235f51a67beff794eb0f44a438.jpg"
          alt="Contact Reiken"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 sm:px-8 md:px-20 w-full">
          <p className="text-white/60 text-xs uppercase tracking-[0.35em] mb-5 font-light">Reach Out</p>
          <h1 className="text-white text-5xl sm:text-6xl md:text-8xl font-bold leading-tight">
            Get in<br />
            <span className="italic font-light">Touch</span>
          </h1>
          <div className="w-12 h-px bg-white/40 mt-8" />
        </div>
      </section>

      <main className="py-16 sm:py-28 px-6 sm:px-8 md:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-5 gap-12 sm:gap-20">

            {/* Left: Contact Info */}
            <div className="md:col-span-2 space-y-10 sm:space-y-12">
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-black/50 mb-5">Contact Information</p>
                <h2 className="text-2xl sm:text-3xl font-bold leading-tight mb-2">
                  Let's Start a<br />
                  <span className="italic font-light">Conversation</span>
                </h2>
                <div className="w-12 h-0.5 bg-black mt-6" />
              </div>

              <div className="space-y-8 sm:space-y-10">
                {contactDetails.map((detail) => (
                  <div key={detail.label} className="flex gap-4 sm:gap-5">
                    <div className="w-10 h-10 flex items-center justify-center border border-black/15 shrink-0">
                      <i className={`${detail.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs uppercase tracking-widest text-black/50 mb-2 font-light">{detail.label}</p>
                      {detail.href ? (
                        <a
                          href={detail.href}
                          className="text-base sm:text-lg font-medium hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
                        >
                          {detail.lines[0]}
                        </a>
                      ) : (
                        detail.lines.map((line, i) => (
                          <p key={i} className="text-base sm:text-lg font-medium leading-relaxed">{line}</p>
                        ))
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="overflow-hidden border border-black/10 h-48 sm:h-56">
                <iframe
                  title="Reiken Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.2!2d-73.9706!3d40.7589!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c258f9e5e5e5e5%3A0x0!2s969+3rd+Ave%2C+New+York%2C+NY+10022!5e0!3m2!1sen!2sus!4v1700000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>

            {/* Right: Form */}
            <div className="md:col-span-3">
              {formSubmitted ? (
                <div className="flex items-center justify-center h-full min-h-[400px] sm:min-h-[500px]">
                  <div className="text-center">
                    <div className="w-20 h-20 flex items-center justify-center mx-auto mb-8 border-2 border-black">
                      <i className="ri-check-line text-4xl"></i>
                    </div>
                    <h2 className="text-3xl sm:text-4xl font-bold mb-4">Thank You</h2>
                    <p className="text-lg sm:text-xl text-black/60 font-light">We'll be in touch shortly.</p>
                    <button
                      onClick={() => setFormSubmitted(false)}
                      className="mt-10 text-sm uppercase tracking-widest border-b border-black pb-1 hover:opacity-60 transition-opacity cursor-pointer whitespace-nowrap"
                    >
                      Send Another Message
                    </button>
                  </div>
                </div>
              ) : (
                <form
                  id="contact-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-7 sm:space-y-8"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-xs uppercase tracking-widest text-black/60 mb-3">
                        Full Name <span className="text-black">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full border-b border-black/30 focus:border-black px-0 py-3 text-base bg-transparent focus:outline-none transition-colors font-light"
                        placeholder="Michael Kennedy"
                      />
                    </div>
                    <div>
                      <label htmlFor="business" className="block text-xs uppercase tracking-widest text-black/60 mb-3">
                        Business Name
                      </label>
                      <input
                        type="text"
                        id="business"
                        name="business"
                        className="w-full border-b border-black/30 focus:border-black px-0 py-3 text-base bg-transparent focus:outline-none transition-colors font-light"
                        placeholder="Company Inc."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-xs uppercase tracking-widest text-black/60 mb-3">
                        Email Address <span className="text-black">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full border-b border-black/30 focus:border-black px-0 py-3 text-base bg-transparent focus:outline-none transition-colors font-light"
                        placeholder="you@company.com"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-xs uppercase tracking-widest text-black/60 mb-3">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full border-b border-black/30 focus:border-black px-0 py-3 text-base bg-transparent focus:outline-none transition-colors font-light"
                        placeholder="(212) 000-0000"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="project-type" className="block text-xs uppercase tracking-widest text-black/60 mb-3">
                      Project Type
                    </label>
                    <select
                      id="project-type"
                      name="project-type"
                      className="w-full border-b border-black/30 focus:border-black px-0 py-3 text-base bg-transparent focus:outline-none transition-colors font-light cursor-pointer appearance-none"
                    >
                      <option value="">Select a project type</option>
                      <option value="retail-construction">Retail Construction</option>
                      <option value="shop-in-shop">Shop-in-Shop Installation</option>
                      <option value="commercial-renovation">Commercial Renovation</option>
                      <option value="government">Government Project</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-xs uppercase tracking-widest text-black/60 mb-3">
                      Project Details <span className="text-black">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={5}
                      maxLength={500}
                      required
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border-b border-black/30 focus:border-black px-0 py-3 text-base bg-transparent focus:outline-none transition-colors resize-none font-light"
                      placeholder="Tell us about your project..."
                    />
                    <p className="text-xs text-black/40 text-right mt-1">{charCount}/500</p>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-black text-white py-4 sm:py-5 text-sm font-semibold uppercase tracking-widest hover:bg-white hover:text-black border-2 border-black transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 mt-4"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
